import type { RenderComponentProps } from "replo-runtime/shared/types";
import type { Component } from "schemas/component";
import type { BundleDiscountThreshold } from "schemas/generated/bundleDiscounts";

import React from "react";

import { useBundleBuilder } from "../BundleBuilderContext";

const getCustomProps = (props: Component["props"]) => {
  const { discountLabel, itemsLabel } = props;
  return {
    discountLabel: (discountLabel as "string" | null) ?? "off",
    itemsLabel: (itemsLabel as "string" | null) ?? "items",
  };
};
export const BundleTierSelector: React.FC<RenderComponentProps> = ({
  component,
  componentAttributes,
}) => {
  const { discountLabel, itemsLabel } = getCustomProps(component.props);
  const {
    isTiered,
    bundleDiscountThresholds,
    selectedBundleDiscountThreshold,
    formatPrice,
    setSelectedBundleDiscountThreshold,
    getBundleTotalQuantity,
  } = useBundleBuilder();

  if (isTiered || bundleDiscountThresholds.length < 2) {
    return null;
  }

  const currentBundleTotalQuantity = getBundleTotalQuantity();

  const formatDiscount = (threshold: BundleDiscountThreshold) => {
    const { discount } = threshold;
    if (discount.type === "percentageOff") {
      return `${discount.value}% ${discountLabel}`;
    }
    return `${formatPrice(String(discount.value))} ${discountLabel}`;
  };

  return (
    <div
      {...componentAttributes}
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "10px",
        ...componentAttributes?.style,
      }}
    >
      {bundleDiscountThresholds.map((threshold) => {
        const isSelected =
          selectedBundleDiscountThreshold.quantity === threshold.quantity;

        return (
          <label
            key={`tier-${threshold.quantity}`}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "10px 16px",
              border: `2px solid ${isSelected ? "#000" : "#e1e1e1"}`,
              backgroundColor: isSelected ? "#f5f5f5" : "white",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "400",
              minWidth: "120px",
            }}
          >
            <input
              type="radio"
              name="bundleTier"
              checked={isSelected}
              disabled={currentBundleTotalQuantity > threshold.quantity}
              onChange={() => setSelectedBundleDiscountThreshold(threshold)}
              style={{ display: "none" }}
            />
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                border: "2px solid #000",
                marginRight: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isSelected && (
                <div
                  style={{
                    display: "block",
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: "#000",
                  }}
                />
              )}
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: "16px", fontWeight: "600" }}>
                {threshold.quantity} {itemsLabel}
              </div>
              <div style={{ fontSize: "16px" }}>
                {formatDiscount(threshold)}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
};
